// Module: micPermissionGestureRequest
// -----------------------------------------------------------------
// Receives:  a synchronous call from a tap handler (lesson start / restart)
// Produces:  one pending mic-permission request that later callers wait on
// Owns:      the pending gesture request
// Does NOT:  capture audio or keep a stream open (→ useMicrophone.initializeAudio)
//            show any UI (→ MicPermissionModal via useMicrophone.showMicModal)
// -----------------------------------------------------------------
//
// iPad Safari rejects a getUserMedia that does not come from a user gesture
// about a second after showing the prompt, so the request has to be made inside
// the tap. A second request while one is pending rejects both, so every later
// caller waits on this one instead of firing its own.

let pendingRequest: Promise<void> | null = null;

/**
 * Input:   none — must be called synchronously inside a tap handler.
 * Output:  a promise that settles (never rejects) once the student answers.
 * Mutates: pendingRequest.
 *
 * Tracks are stopped at once: only the grant is needed, and a pre-assessment
 * may sit between this tap and the lesson.
 */
export function requestMicPermissionFromGesture(): Promise<void> {
  if (pendingRequest) return pendingRequest;
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.getUserMedia
  ) {
    return Promise.resolve();
  }

  pendingRequest = navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((stream) => {
      stream.getTracks().forEach((track) => track.stop());
    })
    .catch(() => {
      // initializeAudio requests again and reports failures via the mic modal.
    })
    .finally(() => {
      pendingRequest = null;
    });
  return pendingRequest;
}

/** Resolves once the pending gesture request settles, or at once if none is. */
export function waitForPendingMicPermission(): Promise<void> {
  return pendingRequest ?? Promise.resolve();
}

/**
 * The pending request, or null. For callers inside a tap, which must not await
 * a settled promise before their own getUserMedia.
 */
export function getPendingMicPermissionRequest(): Promise<void> | null {
  return pendingRequest;
}

/** Test-only: forget the pending request. */
export function resetGestureMicPermissionForTests(): void {
  pendingRequest = null;
}
